package com.arena_web.pages.profile

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.core.viewmodel.UserProfileViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.*
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.navigation.Routes
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

@Page("/profile/{id}")
@Composable
fun UserProfilePage() {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val userId = ctx.route.path.substringAfterLast("/")
    val currentUserId = Storage.get(StorageKeys.USER_ID)
    val isOwnProfile = currentUserId == userId

    val viewModel = rememberViewModel(key = userId) {
        UserProfileViewModel(
            userId = userId,
            userRepository = container.publicUserRepository,
            teamRepository = container.publicTeamRepository,
            followRepository = container.followRepository,
            eventRepository = container.publicEventRepository,
            cardRepository = container.cardRepository,
        )
    }

    val state by viewModel.uiState.collectAsState()

    // Unfollow is destructive (drops the chat permission as a side-effect on
    // the backend), so route the click through a confirmation dialog before
    // firing the API call.
    var pendingUnfollow by remember { mutableStateOf(false) }

    // Read-only interest-note dialog target — matches the own-profile flow
    // (Profile.kt). Tapping a Focus / Activity chip with an attached note
    // opens DetailsPreferenceDialog so the visitor can read it.
    var viewNoteTarget by remember { mutableStateOf<String?>(null) }

    var sportName by remember { mutableStateOf(Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all") }
    val backgroundImage = AppIcons.statisticBackground(sportName)

    AppPageLayout(
        activeTab = SidebarTab.Profile,
        backgroundImage = backgroundImage,
        onSportChanged = {
            sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
            viewModel.reloadSportData()
        }
    ) {
        // Phones render the read-only mobile profile (Figma 8636:332042) — the
        // same content the in-app bottom sheet uses, here as a full page for
        // deep links. The desktop 3-column layout below is the wide-viewport path.
        if (isMobile()) {
            com.arena_web.ui.sections.profile.mobile.UserProfileMobile(
                userId = userId,
                externalViewModel = viewModel,
            )
            return@AppPageLayout
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(26, 40)
                .sGap(16)
        ) {
            if (state.isLoading) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    // Intentionally empty — no loading indicator (user pref).
                }
            } else if (state.error != null) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        state.error ?: t.pageSomethingWentWrong,
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            } else {
                // Following / Followers popups — read-only on other users'
                // profiles. Dialog owns its own paging + debounced server
                // search; we just adapt the target user's endpoint.
                val followRepo = container.followRepository
                if (state.showFollowingDialog && followRepo != null) {
                    com.arena_web.ui.components.overlay.FollowListDialog(
                        mode = com.arena_web.ui.components.overlay.FollowListMode.Following,
                        initialCount = state.followingCount ?: 0L,
                        loadPage = { page, search ->
                            val params = buildMap {
                                put("page", "$page")
                                put("size", "20")
                                if (!search.isNullOrBlank()) put("search", search)
                            }
                            followRepo.getFollowing(userId, params)
                        },
                        onUserClick = { u ->
                            viewModel.closeFollowingDialog()
                            com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, u.userId)
                        },
                        onDismiss = { viewModel.closeFollowingDialog() }
                    )
                }
                if (state.showFollowersDialog && followRepo != null) {
                    com.arena_web.ui.components.overlay.FollowListDialog(
                        mode = com.arena_web.ui.components.overlay.FollowListMode.Followers,
                        initialCount = state.followersCount ?: 0L,
                        loadPage = { page, search ->
                            val params = buildMap {
                                put("page", "$page")
                                put("size", "20")
                                if (!search.isNullOrBlank()) put("search", search)
                            }
                            followRepo.getFollowers(userId, params)
                        },
                        onUserClick = { u ->
                            viewModel.closeFollowersDialog()
                            com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, u.userId)
                        },
                        onDismiss = { viewModel.closeFollowersDialog() }
                    )
                }

                // Unfollow confirmation popup — only shown when the viewer
                // taps the secondary Unfollow pill. Builds a FollowUserDto
                // shim from the loaded profile so the existing dialog can
                // render the avatar / name without a separate component.
                if (pendingUnfollow) {
                    val u = state.user
                    if (u == null) {
                        pendingUnfollow = false
                    } else {
                        val confirmUser = com.arena_web.data.dto.FollowUserDto(
                            userId = u.id,
                            username = u.username,
                            fullName = u.fullName,
                            avatarUrl = u.avatarUrl,
                            cardImageUrl = u.mainProfileCard?.cardImageUrl,
                            cardAvatarUrl = u.mainProfileCard?.avatarUrl,
                            followedAt = "",
                        )
                        val name = u.fullName?.takeIf { it.isNotBlank() }
                            ?: u.username.takeIf { !it.contains('@') }
                            ?: t.feedThisUser
                        com.arena_web.ui.components.overlay.FollowConfirmDialog(
                            user = confirmUser,
                            title = t.feedUnfollowTitle(name),
                            subtitle = t.feedUnfollowSubtitle,
                            confirmLabel = t.feedUnfollow,
                            onConfirm = {
                                viewModel.unfollow()
                                pendingUnfollow = false
                            },
                            onCancel = { pendingUnfollow = false },
                        )
                    }
                }

                // Back row
                Row(
                    modifier = Modifier
                        .sGap(8)
                        .cursor(Cursor.Pointer)
                        .onClick { window.history.back() },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        src = AppIcons.ChevronForward,
                        modifier = Modifier.styleModifier {
                            property("transform", "rotate(180deg)")
                        }
                    )
                    SpanText(
                        t.commonBack,
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }

                // 3-column layout (own/other share the same redesign)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .sGap(20)
                        .styleModifier { property("align-items", "stretch") }
                ) {
                    OverviewSidebar(
                        user = state.user,
                        sportProfile = state.sportProfile,
                        aggregateRating = state.aggregateRating,
                        followersCount = state.followersCount,
                        followingCount = state.followingCount,
                        isOwnProfile = isOwnProfile,
                        isFollowing = state.isFollowing,
                        onFollowClick = if (!isOwnProfile) {
                            {
                                if (state.isFollowing) pendingUnfollow = true
                                else viewModel.follow()
                            }
                        } else null,
                        onChatClick = if (!isOwnProfile && state.isFollowing) {
                            {
                                AnalyticsTracker.trackClick("open_chat", screen = "user_profile", params = mapOf("user_id" to userId))
                                val encode: (String) -> String = { s -> js("encodeURIComponent")(s) as String }
                                val params = buildString {
                                    append("${Routes.CHAT}?user=$userId")
                                    state.user?.fullName?.let { append("&name=${encode(it)}") }
                                    state.user?.avatarUrl?.let { append("&avatar=${encode(it)}") }
                                }
                                ctx.router.navigateTo(params)
                            }
                        } else null,
                        onFollowingClick = { viewModel.openFollowingDialog() },
                        onFollowersClick = { viewModel.openFollowersDialog() },
                        languages = state.languages,
                        interestSports = state.interestSports,
                        interestActivities = state.interestActivities,
                        interestLifestyle = state.interestLifestyle,
                        interestNotes = state.interestNotes,
                        onInterestNoteClick = { id -> viewNoteTarget = id },
                        awards = state.awards,
                        modifier = Modifier.styleModifier { property("flex", "600 1 0") }
                    )

                    Column(
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier {
                                property("flex", "561 1 0")
                                property("justify-content", "flex-end")
                            }
                    ) {
                        RecentMatchesSection(
                            matches = state.matchHistory,
                            currentPage = state.matchHistoryPage,
                            totalPages = matchHistoryTotalPages(state.matchHistoryTotal),
                            onPageChange = { viewModel.loadMatchHistoryPage(it) },
                            isLoading = state.matchHistoryLoading,
                        )
                    }

                    PlayerHubSection(
                        sportName = sportName,
                        sportId = state.sportProfile?.sportId ?: "",
                        isOwnProfile = isOwnProfile,
                        profileUserId = userId,
                        // Share the same CardRepository; the ViewModel uses
                        // getUserCards(profileUserId) when not on own profile.
                        cardRepository = container.cardRepository,
                        loadPosts = if (isOwnProfile) {
                            container.userPostRepository?.let { repo ->
                                suspend {
                                    repo.getMyPosts(mapOf("page" to "1", "size" to "20"))
                                        .getOrNull()?.items.orEmpty()
                                }
                            }
                        } else {
                            container.publicUserRepository.let { repo ->
                                suspend {
                                    repo.getUserPosts(userId, mapOf("page" to "1", "size" to "20"))
                                        .getOrNull()?.items.orEmpty()
                                }
                            }
                        },
                        canCreatePost = isOwnProfile && container.userPostRepository != null,
                        activeCardId = state.user?.mainProfileCard?.userCardId,
                        statsCharts = state.statsCharts,
                        goalsChartPeriod = state.goalsChartPeriod,
                        assistsChartPeriod = state.assistsChartPeriod,
                        onGoalsPeriodChanged = { viewModel.changeGoalsChartPeriod(it) },
                        onAssistsPeriodChanged = { viewModel.changeAssistsChartPeriod(it) },
                        userName = state.user?.fullName ?: state.user?.username ?: "",
                        userAvatarUrl = state.user?.avatarUrl,
                        sportProfile = state.sportProfile,
                        modifier = Modifier.styleModifier { property("flex", "639 1 0") }
                    )
                }
            }
        }

        // Read-only preference-details dialog — mirrors Profile.kt. Mounted at
        // the AppPageLayout scope so its fixed overlay covers the viewport.
        val noteTarget = viewNoteTarget
        val targetNote = noteTarget?.let { state.interestNotes[it] }.orEmpty()
        if (noteTarget != null && targetNote.isNotBlank()) {
            com.arena_web.ui.components.overlay.DetailsPreferenceDialog(
                option = com.arena_web.ui.sections.profile.edit.InterestCatalog.lookup(noteTarget),
                note = targetNote,
                onDismiss = { viewNoteTarget = null }
            )
        }
    }
}
